/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

// Ignored due to popup window
/* istanbul ignore file */

// System defined variables
import React, { useState, useEffect } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { useConfirm } from 'material-ui-confirm'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { DialogActions } from '@mui/material'
import { isArray } from 'lodash'

// Custom defined variables.
import TextBox from '../../components/base/textBox/TextBox'
import AutoComplete from '../../components/base/autoComplete/AutoComplete'
import { fetchDataSelector } from '../../components/recoil/selectors/selectors'
import ButtonComponent from '../../components/base/button/Button'
import apiEndPoints from '../../../models/api/apiEndpoints'
import { ConfirmPopupRendererObject } from '../../components/custom/confirmPopupRenderer/ConfirmPopupRenderer'
import utils from '../../../utils/utils'
import {
  toastMessage,
  axiosConfiguration,
  userDetail,
  selectedContractDetails,
} from '../../components/recoil/atoms/atoms'
import './HaloSupportTicket.css'
import Multiline from '../../components/base/multilineText/MultilineText'
import DropZoneNew from '../../components/base/dropZone/DropZone'
import activeCircle from '../../../assets/static/activeCircle.svg'
import inactiveCircle from '../../../assets/static/inactiveCircle.svg'

const HaloSupportTicket = ({ onChangedPopup }) => {
  const showToastMessage = useSetRecoilState(toastMessage)
  const getCoreData = useRecoilValue(fetchDataSelector)
  const loggedUserDetails = useRecoilValue(userDetail)
  const configVariable = useRecoilValue(axiosConfiguration)
  const selectedContractData = useRecoilValue(selectedContractDetails)
  const [userSelectionPopup, setUserSelectionPopup] = useState({
    open: false,
    userData: [],
  })
  const [showTicketPopup, setShowTicketPopup] = useState({
    popupType: 'null',
    status: false,
  })
  const confirmAlert = useConfirm()
  const [imageSrc, setImageSrc] = useState('')
  const [isDisableSave, setIsDisableSave] = useState(true)
  const [haloAuthTokenData, setHaloAuthTokenData] = useState(null)
  const [haloAuthTokenDataUserCreate, setHaloAuthTokenDataUserCreate] =
    useState(null)

  const [haloLookupData, setHaloLookupData] = useState({
    ticketCategory: [],
    ticketUrgency: [
      {
        id: 1,
        name: 'Critical',
      },
      {
        id: 2,
        name: 'High',
      },
      {
        id: 3,
        name: 'Medium',
      },
      {
        id: 4,
        name: 'Low',
      },
    ],
    ticketImpact: [
      {
        id: 1,
        name: 'Critical - Loss To All Services',
      },
      {
        id: 2,
        name: 'High - Loss to a Key Service',
      },
      {
        id: 3,
        name: 'Medium - Moderate Loss To A Key Service',
      },
      {
        id: 4,
        name: 'Low - Single User, Minor or No Impediment To Key Services',
      },
    ],
  })

  const configVariables = configVariable?.VariableSettings

  const staticCode = {
    temName: 'Freedom support',
    grandType: 'client_credentials',
    headerTitle: 'Support Request',
    btnTextSave: 'Send',
    btnTextCancel: 'Cancel',
    defaultTeam: 'Freedom support',
    clientName: 'Marston Holdings',
    chooseFile: 'Choose File',
    dropFile: 'Drop files here',
    takeScreenShot: 'or Take a Screenshot',
    errorMessage: 'This Service is temporarily unavailable, Try again later',
    ticketContent:
      'Thank you for reaching out. Our support team has received your request and will get back to you via email shortly.',
    successHeader: 'Request Submitted!',
    errorHeader: 'Request failed',
    taskPriority: 3,
    invalidFileFormat:
      'Invalid file format, accept only .png, .svg, .jpg files',
    userLogError: 'User log activity failed',
    summaryFieldLength: 75,
    descriptionFieldLength: 5000,
    category_1: 'Support',
    userNotFOundError: 'User not found, Please register in HALO',
    envs: {
      production: 'Production',
      uat: 'UAT',
      dm: 'Data Migration',
      preprod: 'Pre-Production',
      dev: 'Development',
      test: 'Test',
    },
    mnpsCategory: 'Freedom>Release>Change Request',
    applyIQCategory: 'ApplyIQ>Release>Change Request',
  }

  const initialInputValuesState = {
    emailID: loggedUserDetails?.preferred_username || loggedUserDetails?.emails,
    ticketCategory: '',
    description: '',
    attachedImages: [],
    ticketUrgency: '',
    ticketImpact: '',
    organisation_id: '',
    userId: null,
    category_1: staticCode?.category_1,
  }

  const initialFieldAlertState = {
    emailID: '',
    ticketCategory: '',
    description: '',
    summary: '',
  }

  const [inputValues, setInputValues] = useState({
    ...initialInputValuesState,
  })
  const [inputValuesFieldAlert, setInputValuesFieldAlert] = useState({
    ...initialFieldAlertState,
  })

  useEffect(() => {
    // get Reason data from api to be chagne

    const getTicketType = async () => {
      const ticketTypes = await getCoreData(
        apiEndPoints?.haloIntegration?.getCaseCategory?.method,
        apiEndPoints?.haloIntegration?.getCaseCategory?.url
      )

      if (ticketTypes?.status === utils?.apiResponseCode?.successCode) {
        if (ticketTypes?.data && isArray(ticketTypes?.data)) {
          setHaloLookupData((prevData) => ({
            ...prevData,
            ticketCategory: ticketTypes?.data?.filter(
              (ticketType) =>
                ticketType?.default_team === staticCode?.defaultTeam &&
                !ticketType?.name?.toString()?.toLowerCase()?.includes('azure')
            ),
          }))
        }
      }
    }

    getTicketType()
  }, [])

  useEffect(() => {
    setIsDisableSave(disableSaveButton())
  }, [inputValues])

  function onChangedPopupClosed() {
    onChangedPopup()
  }

  const onChangeHandler = (event, params) => {
    const { id, value } = params
    if (
      id?.toString()?.toLowerCase() === 'summary' &&
      value?.toString()?.length > staticCode?.summaryFieldLength
    ) {
      setInputValuesFieldAlert((prevValues) => {
        return {
          ...prevValues,
          [id]: `Maximum ${staticCode?.summaryFieldLength} characters only allowed`,
        }
      })
    } else if (
      id?.toString()?.toLowerCase() === 'description' &&
      value?.toString()?.length > staticCode?.descriptionFieldLength
    ) {
      setInputValuesFieldAlert((prevValues) => {
        return {
          ...prevValues,
          [id]: `Maximum ${staticCode?.descriptionFieldLength} characters only allowed`,
        }
      })
    } else {
      setInputValuesFieldAlert((prevValues) => ({
        prevValues,
        [id]: null,
      }))
      setInputValues((prevValues) => {
        return { ...prevValues, [id]: value }
      })
    }
  }

  function disableSaveButton() {
    if (
      inputValues?.emailID?.length > 0 &&
      utils?.isValidEmailAddress(inputValues?.emailID) &&
      inputValues?.ticketCategory &&
      inputValues?.description?.toString()?.length > 0 &&
      inputValues?.summary?.toString()?.length > 0 &&
      inputValues?.ticketImpact?.toString()?.length > 0 &&
      inputValues?.ticketUrgency?.toString()?.length > 0
    ) {
      return false
    }

    return true
  }

  const checkFormChanged = () => {
    if (
      (inputValues?.emailID?.length > 0 &&
        inputValues?.emailID !== initialInputValuesState?.emailID) ||
      inputValues?.ticketCategory ||
      inputValues?.description?.toString()?.length > 0 ||
      inputValues?.attachedImages?.length > 0 ||
      inputValues?.ticketImpact?.toString()?.length > 0 ||
      inputValues?.ticketUrgency?.toString()?.length > 0 ||
      inputValues?.summary?.toString()?.length > 0
    ) {
      return true
    }

    return false
  }

  const attachmentPostDataTicketCreation = () => {
    const imagePostData = []

    inputValues?.attachedImages?.forEach((img) => {
      const imgData = {
        filename: img?.fileName,
        base64Content: img?.fileData,
        isshow: true,
      }
      imagePostData.push(imgData)
    })

    return imagePostData
  }

  const ticketPost = async (userList = {}) => {
    let env = staticCode?.envs?.production
    const envName =
      configVariable?.VariableSettings?.BackOfficeUIURL?.toString()?.toLowerCase()

    if (envName?.includes('localhost') || envName?.includes('development'))
      env = staticCode?.envs?.dev
    else if (envName?.includes('backoffice-test')) env = staticCode?.envs?.test
    else if (envName?.includes('backoffice-uat')) env = staticCode?.envs?.uat
    else if (envName?.includes('datamigration-backoffice'))
      env = staticCode?.envs?.dm
    else if (envName?.includes('preprod-freedom'))
      env = staticCode?.envs?.preprod

    const selectedCategoryName =
      haloLookupData?.ticketCategory?.filter(
        (categoryData) => categoryData?.id === inputValues?.ticketCategory
      )?.[0]?.name || ''

    const postData = {
      dateOccurred: utils?.returnCurrentDateTimeInUTC(), // Current date
      team: staticCode?.temName, // Team
      clientName: selectedContractData?.contractName, // Client Name
      category1: inputValues?.category_1, // Reason
      details: inputValues?.description, // Description
      userEmail: inputValues?.emailID, // Email
      ticketTypeId: inputValues?.ticketCategory, // Category ID
      summary: inputValues?.summary, // Title field
      priorityId: staticCode?.taskPriority, // Priority as P3
      userId: inputValues?.userId || userList?.id, // validate the user name in user API if available then, pass the user ID
      impact: inputValues?.ticketImpact, // Impact
      urgency: inputValues?.ticketUrgency,
      customFields: [
        {
          name: 'CFImpact',
          value:
            haloLookupData?.ticketImpact?.filter(
              (impact) => impact?.id === inputValues?.ticketImpact
            )?.[0]?.name || '',
        },
        {
          name: 'CFUrgency',
          value:
            haloLookupData?.ticketUrgency?.filter(
              (urgency) => urgency?.id === inputValues?.ticketUrgency
            )?.[0]?.name || '',
        },
        {
          name: 'CFEnvironment',
          value: env,
        },
      ],
      attachments: attachmentPostDataTicketCreation(),
      category3: selectedCategoryName?.toLowerCase()?.includes('applyiq')
        ? staticCode?.applyIQCategory
        : staticCode?.mnpsCategory,
    }

    const createdTicket = await getCoreData(
      apiEndPoints?.haloIntegration?.ticketCreate?.method,
      apiEndPoints?.haloIntegration?.ticketCreate?.url,
      postData
    )

    if (createdTicket?.status === utils?.apiResponseCode?.successCode) {
      const addUserLogActivity = await getCoreData(
        apiEndPoints?.haloIntegration?.eventLogged?.method,
        apiEndPoints?.haloIntegration?.eventLogged?.url,
        {
          contractId: selectedContractData?.contractId || null,
        }
      )

      if (addUserLogActivity?.status !== utils?.apiResponseCode?.successCode) {
        showToastMessage({
          message: staticCode?.userLogError,
          status: utils?.toastStatus?.error,
        })
      }
      setInputValues(() => initialInputValuesState)
      setShowTicketPopup(() => ({
        popupType: utils?.toastStatus?.success,
        status: true,
      }))
    } else {
      setShowTicketPopup(() => ({
        popupType: utils?.toastStatus?.error,
        status: true,
      }))
    }
  }

  const createNewUserInHalo = async () => {
    // Add user into HALO system.
    // userExist = false
    let addUserDetails = {}
    const addUserPostData = {
      name: loggedUserDetails?.name || loggedUserDetails?.userName,
      siteName: 'Main',
      clientName: selectedContractData?.contractName,
      firstName: loggedUserDetails?.given_name || loggedUserDetails?.name,
      surname: loggedUserDetails?.family_name,
      emailAddress:
        loggedUserDetails?.preferred_username || loggedUserDetails?.emails,
      inactive: false,
      priorityId: 0,
      linkedAgentId: 0,
      isServiceAccount: false,
      isImportantContact: false,
      messageGroupId: -1,
      use: 'user',
      sendWelcomeEmail: true,
      resetPassword: true,
    }

    addUserDetails = await getCoreData(
      apiEndPoints?.haloIntegration?.addUserDetail?.method,
      apiEndPoints?.haloIntegration?.addUserDetail?.url,
      addUserPostData,

      false // consider unauthorized error
    )
    if (
      addUserDetails?.status === utils?.apiResponseCode?.successCode ||
      addUserDetails?.status === utils?.apiResponseCode?.successResponse
    ) {
      setInputValues((prevValues) => ({
        ...prevValues,
        userId: addUserDetails?.data?.id,
      }))
      ticketPost(addUserDetails?.data)
    } else {
      showToastMessage({
        message: staticCode?.userNotFOundError,
        status: utils?.toastStatus?.error,
      })
    }
  }

  // Save button click
  const onSaveHandler = async () => {
    const userDetails = await getCoreData(
      apiEndPoints?.haloIntegration?.validateUserDetails?.method,
      apiEndPoints?.haloIntegration?.validateUserDetails?.url?.replace(
        '__search__',
        loggedUserDetails?.name || loggedUserDetails?.userName
      )
    )

    // let userExist = false
    if (userDetails?.status === utils?.apiResponseCode?.successCode) {
      const userList = userDetails?.data?.users?.filter(
        (user) =>
          (user?.emailaddress === loggedUserDetails?.emails ||
            user?.emailaddress === loggedUserDetails?.preferred_username) &&
          user?.azureoid?.toString()?.length > 0 &&
          user?.onpremise_activedirectory_dn
            ?.toString()
            ?.includes('DC=itservices')
      )

      if (userList?.length > 1 && userDetails?.data?.record_count > 0) {
        setUserSelectionPopup({
          open: true,
          userData: userList?.map((user) => ({
            ...user,
            userEmail: `${user?.emailaddress} - ${user?.client_name}`,
          })),
        })
      } else if (userList?.length === 1) {
        ticketPost(userList?.[0])
      } else {
        if (userDetails?.data?.record_count > 0) {
          setUserSelectionPopup({
            open: true,
            userData: userDetails?.data?.users?.map((user) => ({
              ...user,
              userEmail: `${user?.emailaddress} - ${user?.client_name}`,
            })),
          })
        } else createNewUserInHalo()
      }
    } else {
      showToastMessage({
        message: staticCode?.userNotFOundError,
        status: utils?.toastStatus?.error,
      })
    }
  }

  /* istanbul ignore next */
  const onClosePopup = async () => {
    if (await getCancelConfirmation()) {
      onChangedPopupClosed()
    }
  }

  /* istanbul ignore next */
  const getCancelConfirmation = async () => {
    let returnValue = true
    if (checkFormChanged()) {
      returnValue = await confirmAlert({
        ...ConfirmPopupRendererObject(null, utils?.dataSaveWarnMessage),
      })
        .then(() => {
          return true
        })
        .catch(() => {
          return false
        })
    }
    return returnValue
  }

  function getBase64(file, filePreview) {
    if (file) {
      var reader = new FileReader()
      reader.readAsDataURL(file)
      return (reader.onload = function () {
        const fileObj = {
          fileName: file?.path,
          filePath: filePreview,
          fileData: reader?.result,
        }

        setInputValues((previousState) => ({
          ...previousState,
          attachedImages: [...previousState.attachedImages, fileObj],
        }))
      })
    }
  }

  const updateFiles = (file) => {
    if (file && file?.length > 0) {
      file?.forEach(async (eachFile) => {
        if (Number(eachFile?.size) / 1024 / 1024 <= 1) {
          let newFile = {}
          if (!(file && eachFile?.preview)) {
            newFile = { ...eachFile, preview: URL.createObjectURL(eachFile) }
          }

          await getBase64(
            eachFile,
            eachFile?.preview ? eachFile?.preview : newFile?.preview
          )
        } else {
          showToastMessage({
            message: utils?.branding?.error?.fileTooBig,
            status: utils?.toastStatus?.error,
          })
        }
      })
    }
  }

  const screenshotPage = () => {
    document.getElementById('supportTicketPopupWindow').style.display = 'none'
    const screenshotTarget = document.body
    html2canvas(screenshotTarget).then(async (canvas) => {
      const dataURL = canvas.toDataURL()
      const base64Response = await fetch(dataURL)
      const blobURL = await base64Response.blob()
      const fileObj = {
        fileName: `screenCapture_${utils?.getCurrentDateTime()}.${
          blobURL?.type?.toString()?.split('/')[1]
        }`,
        filePath: blobURL,
        fileData: dataURL,
      }
      document.getElementById('supportTicketPopupWindow').style.display = 'flex'

      setInputValues((previousState) => ({
        ...previousState,
        attachedImages: [...previousState.attachedImages, fileObj],
      }))
    })
  }

  const removeFile = (fileLocation) => {
    const existAttachedImages = [...inputValues?.attachedImages]
    existAttachedImages?.splice(fileLocation, 1)
    setInputValues((prevValues) => ({
      ...prevValues,
      attachedImages: existAttachedImages,
    }))
  }

  const userSelectionPopupSubmitHandler = (actionType) => {
    if (actionType === 'createUser') {
      createNewUserInHalo()
    } else if (actionType === 'submit') {
      setUserSelectionPopup(() => ({ open: false, userData: [] }))
      ticketPost()
    }
  }

  const userSelectionOnChangeHandler = (event, params) => {
    const { id, value } = params
    setInputValues((prevValues) => ({
      ...prevValues,
      [id]: value,
    }))
  }

  const onCloseUserSelectionPopup = () => {
    setUserSelectionPopup(() => ({
      open: false,
      userData: [],
    }))
    setInputValues((prevValues) => ({ ...prevValues, userId: null }))
  }

  return (
    <div className="supportTicketPopupWindow" id="supportTicketPopupWindow">
      <div className="supportPopup">
        <div className="supportTicketSavePopup" id="supportTicketSavePopup">
          <div className="headerSection">
            <div className="headerTitle">{staticCode?.headerTitle}</div>
            <div
              className="popupCloseIcon"
              data-testId="popupCloseIcon"
              onClick={() => {
                onClosePopup()
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="commonContentSection">
            <div className="contentSection">
              <div className="supportTicketContentMainsection">
                <div className="dataSection">
                  <div className="inputControls">
                    <AutoComplete
                      id="ac_ticketCategory"
                      dataTestId={`${
                        haloLookupData?.ticketCategory?.length > 0
                          ? 'ac_ticketCategory'
                          : 'ticketCategory'
                      }`}
                      field={{
                        fieldLabel: 'Category ',
                        fieldValue: 'ticketCategory',
                        placeholder: 'Select Category',
                        dataSource: haloLookupData?.ticketCategory,
                        valueField: 'id',
                        textField: 'name',
                        isSelect: false,
                        className: `colWiseControl ${
                          inputValuesFieldAlert?.ticketCategory
                            ? 'outlineError'
                            : ''
                        }`,
                        fieldAlert: inputValuesFieldAlert?.ticketCategory,
                        popperClassName: 'supportTicket',
                      }}
                      value={inputValues?.ticketCategory}
                      onChangeHandler={onChangeHandler}
                      required={true}
                    />
                  </div>
                  <div className="inputControls">
                    <TextBox
                      id="txt_summary"
                      dataTestId="txt_summary"
                      field={{
                        fieldLabel: 'Title',
                        fieldValue: 'summary',
                        placeholder: 'Enter Title',
                        className: `colWiseControl ${
                          inputValuesFieldAlert?.summary ? 'outlineError' : ''
                        }`,
                        fieldAlert: inputValuesFieldAlert?.summary,
                      }}
                      required={true}
                      value={inputValues?.summary}
                      onChangeHandler={onChangeHandler}
                    />
                  </div>
                  <div className="inputControls">
                    <AutoComplete
                      id="ac_Urgency"
                      dataTestId={`${
                        haloLookupData?.ticketUrgency?.length > 0
                          ? 'ac_Urgency'
                          : 'urgency'
                      }`}
                      field={{
                        fieldLabel: 'Urgency ',
                        fieldValue: 'ticketUrgency',
                        placeholder: 'Select Urgency',
                        dataSource: haloLookupData?.ticketUrgency,
                        valueField: 'id',
                        textField: 'name',
                        isSelect: false,
                        className: `colWiseControl ${
                          inputValuesFieldAlert?.ticketUrgency
                            ? 'outlineError'
                            : ''
                        }`,
                        fieldAlert: inputValuesFieldAlert?.ticketUrgency,
                        popperClassName: 'supportTicket',
                      }}
                      value={inputValues?.ticketUrgency}
                      onChangeHandler={onChangeHandler}
                      required={true}
                    />
                  </div>
                  <div className="inputControls">
                    <AutoComplete
                      id="ac_Impact"
                      dataTestId={`${
                        haloLookupData?.ticketImpact?.length > 0
                          ? 'ac_Impact'
                          : 'ticketImpact'
                      }`}
                      field={{
                        fieldLabel: 'Impact ',
                        fieldValue: 'ticketImpact',
                        placeholder: 'Select Impact',
                        dataSource: haloLookupData?.ticketImpact,
                        valueField: 'id',
                        textField: 'name',
                        isSelect: false,
                        className: `colWiseControl ${
                          inputValuesFieldAlert?.ticketImpact
                            ? 'outlineError'
                            : ''
                        }`,
                        fieldAlert: inputValuesFieldAlert?.ticketImpact,
                        popperClassName: 'supportTicket',
                      }}
                      value={inputValues?.ticketImpact}
                      onChangeHandler={onChangeHandler}
                      required={true}
                    />
                  </div>
                  <div className="inputControls">
                    {utils?.getLabel('Description', true)}
                    <Multiline
                      id="txt_description"
                      dataTestId="txt_description"
                      name="description"
                      placeHolder="Enter Description"
                      fieldValue="description"
                      value={inputValues?.description}
                      isDisablemaxlength={true}
                      minRowsCount="4"
                      maxRowsCount="5"
                      required={true}
                      className={`colWiseControl ${
                        inputValuesFieldAlert?.description ? 'outlineError' : ''
                      }`}
                      onChangeHandler={onChangeHandler}
                      showcount={false}
                      errorMeaaage={inputValuesFieldAlert?.description}
                    />
                  </div>
                </div>
                <div className="imageUploadSection">
                  <div className="inputControls">
                    {utils?.getLabel('Upload Images', false)}
                    <div className="haloSupportImagesMainSection">
                      <div
                        className="haloSupportImagesSubSection"
                        id="haloSupportImages"
                      >
                        <DropZoneNew
                          previewViewer={false}
                          updateFiles={updateFiles}
                          imageSrc={imageSrc}
                          resetImageSrc={() => setImageSrc('')}
                          imageFileName={inputValues?.logoFileName}
                          returnedDropZoneErrorMessage={
                            staticCode?.invalidFileFormat
                          }
                          allowedFormate={{
                            'image/jpeg': ['.jpeg', '.JPEG'],
                            'image/jpg': ['.jpg', '.JPG'],
                            'image/png': ['.png', '.PNG'],
                            'image/svg': ['.svg'],
                          }}
                          maxFilesCount={100}
                          dataTestId="headerBasicLogo"
                          acceptContent="Accepted type: .png, .svg, .jpg | Max. file size: 1Mb."
                          baseStyleApply={true}
                          showDefault="false"
                          fileNameLocation="logoFileLocation"
                          customDropZoneContent={
                            <div className="imageUploadCustomMessage">
                              <div className="mainText">
                                {staticCode?.chooseFile}
                              </div>
                              <div className="subText">or</div>
                              <div className="subText">
                                {staticCode?.dropFile}
                              </div>
                            </div>
                          }
                          customDropZoneContentSubFunctionality={
                            <div className="imageUploadCustomMessage">
                              <div
                                className="mainText"
                                onClick={screenshotPage}
                              >
                                {staticCode?.takeScreenShot}
                              </div>
                            </div>
                          }
                          fromForm={'brandingsetup'}
                          dropZoneErrorMessage={
                            utils?.branding?.error?.invalidFormat
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="inputControls imagecontainerMainSection">
                    {inputValues?.attachedImages?.map((eachImage, imgindex) => {
                      return (
                        <div className="imageContainer">
                          <div className="imageSection">
                            <img
                              src={eachImage?.fileData}
                              alt={eachImage?.fileName}
                            />
                            <div className="uploadImageName">
                              {eachImage?.fileName}
                            </div>
                          </div>
                          <div className="uploadImageRemove">
                            <CloseIcon onClick={() => removeFile(imgindex)} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="footerSection">
            <div className="btn_haloTicket_cancel">
              <ButtonComponent
                Field={{
                  fieldValue: staticCode?.btnTextCancel,
                  fieldLabel: staticCode?.btnTextCancel,
                  variant: 'secondary',
                }}
                id="btn_haloTicket_cancel"
                dataTestId="btn_haloTicket_cancel"
                className="cancelButton"
                onClickHandler={() => {
                  onClosePopup()
                }}
              />
            </div>
            <div className="btn_haloTicket_register">
              <ButtonComponent
                Field={{
                  id: 'btn_haloTicket_add',
                  fieldValue: staticCode?.btnTextSave,
                  fieldLabel: staticCode?.btnTextSave,
                  variant: 'primary',
                  disabled: isDisableSave,
                }}
                id="btn_haloTicket_save"
                dataTestId="btn_haloTicket_save"
                className="btn_haloTicket_add"
                onClickHandler={onSaveHandler}
              />
            </div>
          </div>
        </div>
      </div>

      {showTicketPopup?.status && (
        <div className="supportTicketPopupMainSection ">
          <Dialog
            id="supportTicketDialogPopup"
            open={showTicketPopup?.status}
            className="supportTicketDialogPopupMainSection"
          >
            <DialogContent>
              <div className="supportTicketDialogPopupContentSection">
                <div className="imageSection">
                  {showTicketPopup?.popupType?.toString()?.toLowerCase() ===
                  utils?.toastStatus?.error ? (
                    <div>
                      <img src={inactiveCircle} alt="ErrorIcon" />
                    </div>
                  ) : (
                    <div>
                      <img src={activeCircle} alt="SuccessIcon" />
                    </div>
                  )}
                </div>
                <div className="headingSection">
                  {showTicketPopup?.popupType?.toString()?.toLowerCase() ===
                  utils?.toastStatus?.error ? (
                    <div className="supportTicketHeading">
                      {staticCode?.errorHeader}
                    </div>
                  ) : (
                    <div className="supportTicketHeading">
                      {staticCode?.successHeader}
                    </div>
                  )}
                </div>
                <div className="contentSection">
                  {showTicketPopup?.popupType?.toString()?.toLowerCase() ===
                  utils?.toastStatus?.error ? (
                    <div className="supportTicketContent">
                      {' '}
                      {staticCode?.errorMessage}
                    </div>
                  ) : (
                    <div className="supportTicketContent">
                      {staticCode?.ticketContent}
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              {showTicketPopup?.popupType?.toString()?.toLowerCase() ===
                utils?.toastStatus?.error && (
                <ButtonComponent
                  Field={{
                    fieldValue: 'No',
                    fieldLabel: 'Cancel',
                    variant: 'secondary',
                  }}
                  className="saveButton"
                  onClickHandler={() => {
                    onChangedPopupClosed()
                  }}
                />
              )}
              <ButtonComponent
                Field={{
                  fieldValue: 'Yes',
                  fieldLabel: `${
                    showTicketPopup?.popupType?.toString()?.toLowerCase() ===
                    utils?.toastStatus?.error
                      ? 'Retry'
                      : 'Close'
                  }`,
                  variant: 'primary',
                }}
                className="saveButton"
                onClickHandler={() => {
                  showTicketPopup?.popupType?.toString()?.toLowerCase() ===
                  utils?.toastStatus?.error
                    ? setShowTicketPopup(() => ({
                        popupType: null,
                        status: false,
                      }))
                    : onChangedPopupClosed()
                }}
              />
            </DialogActions>
          </Dialog>
        </div>
      )}

      {userSelectionPopup?.open && (
        <div className="userSelectionPopupMainSection ">
          <Dialog
            id="userSelectionDialogPopup"
            open={userSelectionPopup?.open}
            className="userSelectionDialogPopupMainSection"
          >
            <DialogContent>
              <div className="userSelectionDialogPopupContentSection">
                <div className="headingSection">
                  Select relevant user to the organisation
                  <div
                    className="popupCloseIcon"
                    data-testId="popupCloseIcon"
                    onClick={() => onCloseUserSelectionPopup()}
                  >
                    <CloseIcon style={{ height: '20px' }} />
                  </div>
                </div>
                <div className="contentSection">
                  <div className="inputControls">
                    <AutoComplete
                      id="ac_userList"
                      dataTestId="ac_userList"
                      field={{
                        fieldLabel: 'Select User',
                        fieldValue: 'userId',
                        placeholder: 'Select User',
                        dataSource: userSelectionPopup?.userData,
                        valueField: 'id',
                        textField: 'userEmail',
                        isSelect: false,
                        className: 'colWiseControl',
                        popperClassName: 'userSelection',
                      }}
                      value={inputValues?.userId}
                      onChangeHandler={userSelectionOnChangeHandler}
                      required={true}
                    />
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <ButtonComponent
                Field={{
                  fieldValue: 'createUser',
                  fieldLabel: 'Create User',
                  variant: 'secondary',
                }}
                className="saveButton"
                onClickHandler={() =>
                  userSelectionPopupSubmitHandler('createUser')
                }
              />
              <ButtonComponent
                Field={{
                  fieldValue: 'Yes',
                  fieldLabel: 'Submit',
                  variant: 'primary',
                  disabled: !inputValues?.userId,
                }}
                className="saveButton"
                onClickHandler={() => userSelectionPopupSubmitHandler('submit')}
              />
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  )
}
export default HaloSupportTicket
